import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';
import { border, borderRadius, transparentize } from 'polished';
import { arrayOf, bool, func, oneOf, shape, string } from 'prop-types';

import ChevronDown from '../icons/ChevronDown';
import Box from '../Box';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { ReactCountryFlag } from 'react-country-flag';

const StyledContainer = styled('div')(
  ({ theme, disabled }) => ({
    width: '100%',
    height: '40px',
    display: 'inline-flex',
    borderRadius: theme.radii[2],
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    border: 'none',
    cursor: 'pointer',
    marginTop: '28px',
    boxShadow: `0 0 0 2px ${theme.colors.greyLight} inset`,
    color: theme.colors.dark,
    boxSizing: 'border-box',
    transition: 'box-shadow .3s ease',
    appearance: 'none',
    backgroundColor: theme.colors.greyLight,
    '&[data-focus="true"]': {
      borderRadius: `${theme.radii[2]}px ${theme.radii[2]}px 0 0`
    },
    '& ~ label': {
      transition: 'transform .3s ease',
      display: 'inline-block',
      position: 'absolute',
      top: 0,
      left: 0
    },
    '&[data-focus="true"] ~ label': {
      transform: 'translateX(5px)'
    },
    ...(disabled && {
      boxShadow: `0 0 0 2px ${theme.colors.greyVerylight} inset`,
      backgroundColor: theme.colors.greyVerylight,
      cursor: 'not-allowed'
    })
  }),
  ({ theme }) =>
    variant({
      prop: 'state',
      variants: {
        success: {
          boxShadow: `0 0 0 2px ${theme.colors.blue} inset`,
          backgroundColor: transparentize(0.2, theme.colors.blue)
        },
        error: {
          boxShadow: `0 0 0 2px ${theme.colors.red} inset`,
          backgroundColor: transparentize(0.2, theme.colors.red)
        }
      }
    })
);

const StyledInput = styled('input')(({ theme }) => ({
  outline: 'none',
  border: 'none',
  display: 'inline-block',
  lineHeight: '22px',
  height: 'calc(100% - 4px)',
  width: 'calc(100% - 40px)',
  marginTop: '2px',
  marginLeft: '2px',
  borderRadius: theme.radii[2],
  paddingLeft: theme.space[2],
  cursor: 'inherit',
  background: theme.colors.transparent,
  '&::placeholder': {
    color: theme.colors.grey
  },
  '&:disabled': {
    cursor: 'not-allowed'
  }
}));

const StyledIcon = styled('div')(({ theme, inputFocus }) => ({
  width: '40px',
  height: '40px',
  padding: theme.space[1],
  fontSize: '24px',
  color: theme.colors.greyBluedarker,
  ...(inputFocus && {
    transform: 'rotate(180deg)'
  })
}));

const StyledList = styled('ul')(({ theme, inputFocus }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  display: 'none',
  textAlign: 'left',
  top: 'calc(100% - 2px)',
  maxHeight: '140px',
  zIndex: theme.dropdownIndex,
  background: theme.colors.white,
  overflowY: 'scroll',
  overflowX: 'hidden',
  padding: `${theme.space[1]}px 0`,
  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  ...border('1px', 'solid', theme.colors.greyVerylight),
  borderTop: 'none',
  ...borderRadius('bottom', theme.radii[1]),
  ...(inputFocus && { display: 'block' })
}));

const StyledListItem = styled('li')(
  ({ theme }) => ({
    display: 'flex',
    padding: `${theme.space[1]}px ${theme.space[2]}px`,
    '&:hover': {
      background: transparentize(0.8, theme.colors.main)
    }
  }),
  ({ theme }) =>
    variant({
      variants: {
        empty: {
          color: theme.colors.grey,
          fontStyle: 'italic',
          textAlign: 'center'
        }
      }
    })
);
const useValueAndLabel = function (options, defaultValue) {
  const [selectedValue, setSelectedValue] = useState('');
  const [currentLabel, setCurrentLabel] = useState('');

  useEffect(() => {
    const item = options.find(item => item.value === defaultValue);
    if (item) {
      setCurrentLabel(item.label);
      setSelectedValue(item.value);
    }
  }, [options, defaultValue]);

  return { selectedValue, setSelectedValue, currentLabel, setCurrentLabel };
};

const DropdownField = React.forwardRef(
  ({ options, defaultValue, name, state, onChange, placeholder, onFocus, onBlur, autoComplete }, ref) => {
    let itemSelectedByClick = false;
    let [focus, setFocus] = useState('');
    const cleanedOptions = options || [];
    const [filteredItems, setFilteredItems] = useState(cleanedOptions);
    const { selectedValue, setSelectedValue, currentLabel, setCurrentLabel } = useValueAndLabel(
      cleanedOptions,
      defaultValue
    );
    const focusOn = field => {
      setFocus(field);
      focus = field; // "unFocus" may be called by the other field in the same "render"
    };
    const unFocus = field => {
      if (focus === field) {
        setFocus('');
        onBlur();
      }
    };
    const searchInputRef = useRef();
    const listRef = useRef();
    useClickOutside(listRef, () => unFocus('list'));

    const updateFilteredList = search => {
      const updatedFilterdListed = options.filter(
        item => item.label.includes(search) || item.textLabel.toLowerCase().includes(search)
      );
      setFilteredItems(updatedFilterdListed);
    };

    const toggleList = e => {
      const input = searchInputRef.current;
      e.preventDefault();
      if (focus === '') {
        focusOn('text');
        input.focus();
      } else {
        unFocus(focus);
        if (focus === 'text') {
          input.blur();
        }
      }
    };

    const selectItem = item => {
      itemSelectedByClick = true;
      setCurrentLabel(item.label);
      setSelectedValue(item.value);
      onChange(item.value);
      unFocus('list');
    };

    const resetValue = () => {
      setSelectedValue('');
      setCurrentLabel('');
      onChange('');
    };

    const focusAndSelectText = e => {
      focusOn('text');
      e.target.select();
      onFocus(e);
    };

    const setValueFromLabel = function (label) {
      const itemForLabel = filteredItems.find(item => item.label === label);
      if (!itemForLabel) {
        resetValue();
      }
      if (itemForLabel && itemForLabel.value !== selectedValue) {
        selectItem(itemForLabel);
      }
    };

    const searchUpdated = event => {
      const search = event.target.value;
      setCurrentLabel(search);
      updateFilteredList(search);
    };

    const leaveInput = () => {
      if (!itemSelectedByClick) {
        setValueFromLabel(currentLabel);
      }
      unFocus('text');
      updateFilteredList('');
    };

    const onFocusOnList = e => {
      e.stopPropagation(); //Do not close on scroll
      focusOn('list');
    };

    return (
      <StyledContainer onMouseDown={toggleList} state={state} data-focus={focus}>
        <input type="hidden" ref={ref} name={name} value={selectedValue} />
        <StyledInput
          placeholder={placeholder}
          ref={searchInputRef}
          onChange={searchUpdated}
          onFocus={focusAndSelectText}
          onBlur={leaveInput}
          value={currentLabel}
          autoComplete={autoComplete}
          data-cy={`${name}-search`}
        />
        <StyledIcon inputFocus={focus !== ''}>
          <ChevronDown />
        </StyledIcon>

        <StyledList ref={listRef} inputFocus={focus !== ''} tabIndex="-1" onMouseDown={onFocusOnList}>
          {filteredItems.map(item => (
            <StyledListItem
              key={`${name}_${item.value}`}
              onMouseDown={e => {
                e.stopPropagation(); //Do not focus on list (see parent) after click
                e.preventDefault(); // If not, the next element can not be "focus" (for InputPhone for example)
                selectItem(item);
              }}>
              <Box display="flex" alignItems="center">
                <ReactCountryFlag countryCode={item.value} svg /> &nbsp; {item.textLabel} ({item.label})
              </Box>
            </StyledListItem>
          ))}
        </StyledList>
      </StyledContainer>
    );
  }
);

DropdownField.propTypes = {
  onChange: () => null,
  onFocus: () => null,
  onBlur: () => null
};

DropdownField.propTypes = {
  defaultValue: string,
  options: arrayOf(
    shape({
      label: string,
      value: string
    })
  ).isRequired,
  state: oneOf(['success', 'error', 'default']),
  disabled: bool,
  placeholder: string,
  type: string,
  name: string,
  onChange: func,
  onFocus: func,
  onBlur: func,
  label: string,
  readOnly: bool,
  autoComplete: string
};

export default DropdownField;
